import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import {
    createTheme,
    CssBaseline,
    type Theme,
    type ThemeOptions,
    ThemeProvider,
    useMediaQuery,
} from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { PaletteOptions } from "@mui/material/styles/createPalette";
import {
    createContext,
    type ReactNode,
    useContext,
    useMemo,
    useState,
} from "react";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";

export enum ApplicationMode {
    Dark = "dark",
    Light = "light",
}

export type ThemeContextType = {
    mode: ApplicationMode;
    theme: Theme;
    toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextType>(null!);

let baseThemeOptions = {
    primary: {
        main: "#424EBC",
    },
    secondary: {
        main: "#9848FF",
    },
    error: {
        main: "#9B1C1C",
    },
};

let lightThemePalette: PaletteOptions = {
    mode: "light",
    primary: blue,
    background: {
        default: "#EEEEEE",
        paper: "white",
    },
    text: {
        primary: grey[900],
        secondary: grey[700],
    },
    ...baseThemeOptions,
};

let darkThemePalette: PaletteOptions = {
    mode: "dark",
    primary: blue,
    background: {
        default: "#19191e",
        paper: "#202025",
    },
    text: {
        primary: grey[100],
        secondary: grey[300],
    },
    ...baseThemeOptions,
};

const createAppTheme = (mode: ApplicationMode) => {
    return createTheme({
        direction: "rtl",
        palette:
            mode === ApplicationMode.Light
                ? lightThemePalette
                : darkThemePalette,
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        height: 120,
                        borderRadius: 0,
                        border: "none",
                        position: "sticky",
                        background:
                            mode === ApplicationMode.Light
                                ? "white"
                                : grey[900],
                        color:
                            mode === ApplicationMode.Light ? "black" : "white",
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        fontSize: "15px",
                    },
                },
            },
            MuiStepLabel: {
                styleOverrides: {
                    label: {
                        fontSize: "15px",
                    },
                    root: {
                        fontSize: "15px",
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    root: {
                        fontSize: "15px",
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        height: "100%",
                    },
                },
            },
            MuiCardHeader: {
                styleOverrides: {
                    root: {
                        fontSize: "1rem",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        boxShadow: "none",
                        border: "1px solid",
                        borderColor: "#393542",
                        borderRadius: "12px",
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        border: "none",
                    },
                },
            },
        },
        typography: {
            fontFamily: ["IranSans", "Helvetica"].join(","),
            fontSize: 11,
        },
    } as ThemeOptions);
};

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

export default function ThemeContextProvider(props: { children: ReactNode }) {
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const [mode, setMode] = useState<ApplicationMode>(ApplicationMode.Light);

    const theme = useMemo(() => createAppTheme(mode), [mode]);

    const toggleMode = () =>
        setMode((p) =>
            p === ApplicationMode.Dark
                ? ApplicationMode.Light
                : ApplicationMode.Dark
        );

    return (
        <ThemeContext.Provider
            value={{ theme: theme, mode: mode, toggleTheme: toggleMode }}
        >
            <CacheProvider value={cacheRtl}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    {props.children}
                </ThemeProvider>
            </CacheProvider>
        </ThemeContext.Provider>
    );
}

export const useThemeContext = () => {
    return useContext(ThemeContext);
};
